import { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Input, Button } from "reactstrap";
import { Plus, X } from "react-feather";
import { createBlock, updateBlock } from "../../utils/pickupBlockApi";
import { successToast, errorToast } from "../../shared/Toast";
import { C } from "../reporting/reportingStyles";

const headerStyle = { borderBottom: `2px solid ${C.borderLight}` };
const bodyStyle = { padding: 24, fontFamily: "'Inter', sans-serif" };
const footerStyle = { borderTop: `1px solid ${C.borderLight}`, gap: 8 };
const labelStyle = { fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 6, display: "block" };
const inputStyle = { borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: 13, padding: "8px 12px", color: C.text };
const coralBtnStyle = { background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13 };
const ghostBtnStyle = { background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 600, fontSize: 13 };
const smallBtn = {
  background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, fontWeight: 600, fontSize: 12,
  color: C.textMed, padding: "6px 12px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5,
};

// Create/edit a pickup block. `block` null = create mode; branchId required for create.
// Labels are a free-form list of strings (numbers today, characters/words later); a
// "generate 1..N" helper fills a numeric range quickly.
const BlockModal = ({ isOpen, toggle, onSaved, block, branchId }) => {
  const editing = !!block;
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [labels, setLabels] = useState([""]);
  const [rangeFrom, setRangeFrom] = useState("1");
  const [rangeTo, setRangeTo] = useState("15");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(block?.name || "");
      setIsActive(block?.isActive ?? true);
      const existing = Array.isArray(block?.labels) ? block.labels.map(String) : [];
      setLabels(existing.length ? existing : [""]);
      setRangeFrom("1");
      setRangeTo("15");
    }
  }, [isOpen, block]);

  const addLabelRow = () => setLabels((prev) => [...prev, ""]);
  const removeLabelRow = (index) => setLabels((prev) => prev.filter((_, i) => i !== index));
  const changeLabel = (index, value) => setLabels((prev) => prev.map((l, i) => (i === index ? value : l)));

  // Fills the list with a numeric range; replaces the current labels.
  const generateRange = () => {
    const from = parseInt(rangeFrom, 10);
    const to = parseInt(rangeTo, 10);
    if (Number.isNaN(from) || Number.isNaN(to) || from > to) {
      return errorToast("Enter a valid range (from ≤ to)");
    }
    if (to - from > 500) return errorToast("Range too large (max 500 slots)");
    const generated = [];
    for (let i = from; i <= to; i++) generated.push(String(i));
    setLabels(generated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return errorToast("Block name is required");

    const cleaned = labels.map((l) => String(l).trim()).filter((l) => l.length > 0);
    if (cleaned.length === 0) return errorToast("Add at least one slot label");
    if (new Set(cleaned).size !== cleaned.length) return errorToast("Labels must be unique");

    setSaving(true);
    const payload = { name: name.trim(), labels: cleaned, isActive };
    const result = editing
      ? await updateBlock(block.id, payload)
      : await createBlock({ branchId, ...payload });
    setSaving(false);
    if (result) {
      successToast(editing ? "Block updated" : "Block created");
      onSaved();
      toggle();
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle} style={headerStyle}>
        <span style={{ fontWeight: 700, color: C.text }}>{editing ? "Edit Pickup Block" : "Add Pickup Block"}</span>
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody style={bodyStyle}>
          <FormGroup>
            <label style={labelStyle}>Block Name</label>
            <Input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Front Counter" required />
          </FormGroup>

          <FormGroup>
            <label style={labelStyle}>Slot Labels</label>
            <div style={{ fontSize: 12, color: C.textLight, marginBottom: 12 }}>
              Each box is one slot on this block. Type any value (number, letter, word). Empty boxes are ignored on save.
            </div>

            {/* Quick numeric-range generator — fills the list below with a number range */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: C.textLight }}>Generate numbers</span>
              <Input style={{ ...inputStyle, width: 70 }} type="number" value={rangeFrom} onChange={(e) => setRangeFrom(e.target.value)} />
              <span style={{ fontSize: 12, color: C.textLight }}>to</span>
              <Input style={{ ...inputStyle, width: 70 }} type="number" value={rangeTo} onChange={(e) => setRangeTo(e.target.value)} />
              <button type="button" style={smallBtn} onClick={generateRange}>Generate</button>
            </div>
            <div style={{ fontSize: 11, color: C.textLight, marginBottom: 12, fontStyle: "italic" }}>
              Replaces all slots below. Or build the list by hand with “Add Label”.
            </div>

            {/* Editable label list */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
              {labels.map((lbl, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <Input
                    style={{ ...inputStyle, padding: "6px 10px" }}
                    value={lbl}
                    onChange={(e) => changeLabel(i, e.target.value)}
                    placeholder="empty"
                  />
                  <button
                    type="button"
                    onClick={() => removeLabelRow(i)}
                    title="Remove"
                    style={{ background: "none", border: "none", cursor: "pointer", color: C.textLight, padding: 2 }}
                  >
                    <X size={15} />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" style={{ ...smallBtn, marginTop: 12 }} onClick={addLabelRow}>
              <Plus size={13} /> Add Label
            </button>
          </FormGroup>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: C.text, fontWeight: 600 }}>
            <Input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} style={{ position: "static", margin: 0 }} />
            Active
          </label>
        </ModalBody>
        <ModalFooter style={footerStyle}>
          <Button type="button" onClick={toggle} style={ghostBtnStyle}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving} style={coralBtnStyle}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default BlockModal;
